'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { EVENT_NAMES, nextEvent, nextYearly, remaining, type FixedEvent } from '@/lib/event-dates';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Variant = FixedEvent | 'birthday' | 'wedding' | 'retirement' | 'date' | 'holidays';
const FIXED = Object.keys(EVENT_NAMES) as FixedEvent[];
const isFixed = (v: string): v is FixedEvent => (FIXED as string[]).includes(v);

const pad = (n: number) => String(n).padStart(2, '0');
const longDate = (d: Date) =>
  d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

function icsFile(title: string, at: Date): string {
  const ymd = `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}`;
  const next = new Date(at.getFullYear(), at.getMonth(), at.getDate() + 1);
  const end = `${next.getFullYear()}${pad(next.getMonth() + 1)}${pad(next.getDate())}`;
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Cleanor//Countdown//EN',
    'BEGIN:VEVENT',
    `UID:${ymd}-${title.replace(/\W+/g, '-').toLowerCase()}@cleanor.app`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`,
    `DTSTART;VALUE=DATE:${ymd}`,
    `DTEND;VALUE=DATE:${end}`,
    `SUMMARY:${title}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

export default function BrowserEventCountdown({
  config,
}: {
  config?: { toolSlug?: string; heading?: string; variant?: string };
}) {
  const variant = (config?.variant ?? 'new-year') as Variant;
  const [now, setNow] = useState<Date | null>(null);
  const [dateInput, setDateInput] = useState('');
  const [timeInput, setTimeInput] = useState('00:00');
  const [label, setLabel] = useState('');
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? `${variant}-countdown`, '').catch(() => undefined);
  };

  useEffect(() => {
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const target = useMemo(() => {
    if (!now) return null;
    if (isFixed(variant)) return nextEvent(variant, now);
    if (!dateInput) return null;
    const [y, m, d] = dateInput.split('-').map(Number);
    if (!y || !m || !d) return null;
    if (variant === 'birthday') return nextYearly(m - 1, d, now);
    const [hh, mm] = timeInput.split(':').map(Number);
    return new Date(
      y,
      m - 1,
      d,
      variant === 'date' ? hh || 0 : 0,
      variant === 'date' ? mm || 0 : 0,
    );
  }, [now, variant, dateInput, timeInput]);

  const title = isFixed(variant)
    ? EVENT_NAMES[variant]
    : variant === 'birthday'
      ? label
        ? `${label}'s birthday`
        : 'Your birthday'
      : variant === 'wedding'
        ? 'The wedding'
        : variant === 'retirement'
          ? 'Retirement'
          : label || 'Your date';

  const left = target && now ? remaining(target, now) : null;
  const upcoming = useMemo(
    () =>
      now && variant === 'holidays'
        ? FIXED.map((e) => ({ e, at: nextEvent(e, now) })).sort(
            (a, b) => a.at.getTime() - b.at.getTime(),
          )
        : [],
    [now, variant],
  );

  function download() {
    if (!target) return;
    report();
    const blob = new Blob([icsFile(title, target)], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\W+/g, '-').toLowerCase()}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Countdown</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Countdown'}</h2>
        <p className="tool-live-subtitle">
          Counted to midnight in your own time zone, so it matches your calendar.
        </p>
      </div>
      <div className="tool-live-grid">
        {variant === 'holidays' ? (
          <section className="tool-live-preview" style={{ gridColumn: '1 / -1' }}>
            {upcoming.map(({ e, at }) => {
              // Calendar days from today's midnight, so "tomorrow" reads 1 at any hour.
              const today = now ? new Date(now.getFullYear(), now.getMonth(), now.getDate()) : null;
              const days = today ? Math.round((at.getTime() - today.getTime()) / 86_400_000) : null;
              return (
                <div
                  key={e}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    fontVariantNumeric: 'tabular-nums',
                    padding: '0.35rem 0',
                  }}
                >
                  <span>
                    <strong>{EVENT_NAMES[e]}</strong> · {longDate(at)}
                  </span>
                  <span>
                    {days === null
                      ? ''
                      : days <= 0
                        ? 'Today'
                        : days === 1
                          ? 'Tomorrow'
                          : `${days} days`}
                  </span>
                </div>
              );
            })}
          </section>
        ) : (
          <>
            <section className="tool-live-preview">
              {left && target ? (
                <>
                  <p
                    style={{
                      fontSize: 'clamp(2.25rem, 8vw, 4rem)',
                      fontWeight: 700,
                      lineHeight: 1.05,
                      fontVariantNumeric: 'tabular-nums',
                      margin: 0,
                    }}
                  >
                    {left.days}
                    <small style={{ fontSize: '0.45em', fontWeight: 500 }}> days </small>
                    {pad(left.hours)}:{pad(left.minutes)}:{pad(left.seconds)}
                  </p>
                  <p className="tool-live-subtitle" style={{ margin: '0.25rem 0 0' }}>
                    {left.total === 0
                      ? `${title} is today.`
                      : `until ${title}, ${longDate(target)}`}
                  </p>
                  <div className="tool-live-actions">
                    <button className="button button-secondary" onClick={download} type="button">
                      Add to calendar (.ics)
                    </button>
                  </div>
                </>
              ) : (
                <p className="tool-live-panel-note">
                  {isFixed(variant)
                    ? 'Loading the countdown…'
                    : 'Pick a date to start the countdown.'}
                </p>
              )}
            </section>
            {!isFixed(variant) ? (
              <section className="tool-live-uploader">
                {variant === 'birthday' || variant === 'date' ? (
                  <label className="tool-live-field tool-live-field-full">
                    <span>
                      {variant === 'birthday'
                        ? 'Whose birthday? (optional)'
                        : 'What are you counting down to? (optional)'}
                    </span>
                    <input
                      onChange={(e) => {
                        report();
                        setLabel(e.target.value);
                      }}
                      type="text"
                      value={label}
                    />
                  </label>
                ) : null}
                <label className="tool-live-field">
                  <span>
                    {variant === 'birthday'
                      ? 'Date of birth (any year)'
                      : variant === 'wedding'
                        ? 'Wedding date'
                        : variant === 'retirement'
                          ? 'Retirement date'
                          : 'Date'}
                  </span>
                  <input
                    onChange={(e) => {
                      report();
                      setDateInput(e.target.value);
                    }}
                    type="date"
                    value={dateInput}
                  />
                </label>
                {variant === 'date' ? (
                  <label className="tool-live-field">
                    <span>Time</span>
                    <input
                      onChange={(e) => {
                        report();
                        setTimeInput(e.target.value);
                      }}
                      type="time"
                      value={timeInput}
                    />
                  </label>
                ) : null}
                {variant === 'birthday' && target && now ? (
                  <p className="tool-live-panel-note">
                    Turning {target.getFullYear() - Number(dateInput.slice(0, 4))} on{' '}
                    {longDate(target)}.
                  </p>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
